<template>
  <b-card class="page-view-table">
    <div
      v-if="onProgress"
      class="data-loading d-flex justify-content-center"
    >
      <b-spinner
        class="mt-1"
        label="Loading"
      />
    </div>
    <!-- search input -->
    <div class="custom-search d-flex justify-content-between mb-1">
      <div>
        <h1>{{ settings.title }}</h1>
        <p>{{ settings.description }}</p>
      </div>
      <div class="d-flex justify-content-right">
        <div
          v-for="(filter, index) in settings.filters"
          :key="`report-filter-${index}`"
          class="ml-1"
        >
          <page-date-picker v-if="filter.type === 'daily'" />
          <page-date-range-picker v-if="filter.type === 'daily_range'" />
          <page-week-picker v-if="filter.type === 'weekly'" />
          <page-month-picker v-if="filter.type === 'monthly'" />
        </div>
        <b-form-input
          v-if="rows.length > 1"
          v-model="searchTerm"
          placeholder="Search"
          type="text"
          class="width-200 ml-1"
          @input="handleSearch"
        />
        <span
          v-if="rows.length > 1"
          class="d-inline-block height-40"
        >
          <b-button
            variant="primary"
            class="btn-icon ml-1"
            :disabled="exportingCSV"
            @click="exportCSV"
          >
            <feather-icon
              v-if="!exportingCSV"
              icon="DownloadIcon"
              size="12"
              class="text-muted cursor-pointer"
            />
            <b-spinner
              v-if="exportingCSV"
              label="Loading"
              small
            />
          </b-button>
        </span>
      </div>
    </div>
    <!-- tables -->
    <vue-good-table
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      style-class="vgt-table reports-table"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
        searchFn: searchTable,
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      @on-sort-change="onSortChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <span class="d-flex justify-content-between">
          {{ formatValue(props.column.field, props.formattedRow[props.column.field]) }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">

          <!-- page length -->
          <div
            v-if="props.total > 1"
            class="d-flex align-items-center mb-0 mt-1"
          >
            <span
              class="text-nowrap "
            >
              Showing 1 {{ props.total }} to
            </span>
            <b-form-select
              v-model="pageLength"
              :options="pages"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> of {{ props.total }} entries </span>
          </div>

          <!-- pagination -->
          <div>
            <b-pagination
              v-if="props.total > 1"
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
    BCard, BPagination, BFormGroup, BFormInput, BFormSelect, VBTooltip, BSpinner, BButton,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import { ExportToCsv } from 'export-to-csv'
import store from '@/store'
import { getCoreApiByObjectId } from '@/coreApi/coreApiHelper'
import PageWeekPicker from '@/views/components/filters/PageWeekPicker.vue'
import PageMonthPicker from '@/views/components/filters/PageMonthPicker.vue'
import PageDatePicker from '@/views/components/filters/PageDatePicker.vue'
import PageDateRangePicker from '@/views/components/filters/PageDateRangePicker.vue'
import { recursivelyExecuteQuery } from '@/coreApi/coreApiDataset'

// eslint-disable-next-line import/no-extraneous-dependencies
const moment = require('moment')

export default {
    components: {
        PageDateRangePicker,
        PageDatePicker,
        PageMonthPicker,
        PageWeekPicker,
        BCard,
        VueGoodTable,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BSpinner,
        BButton,
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    props: {
        onProgress: {
            type: Boolean,
            default: true,
        },
        settings: {
            type: Object,
            default: () => {},
        },
        reportData: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            pageLength: 100,
            dir: false,
            pages: ['100', '200'],
            columns: [
                {
                    label: 'Report',
                    field: 'report',
                    sortable: false,
                    hClass: 'page-title-column-th',
                    tdClass: 'text-left align-top page-title-column',
                },
            ],
            rows: [],
            searchTerm: '',
            filterShowTodayPages: false,
            details: null,
            exportingCSV: false,
        }
    },
    computed: {
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },
    watch: {
        reportData() {
            const object = getCoreApiByObjectId(this.reportData, 'data')
            if (
                object
                && 'data' in object
            ) {
                this.columns = []
                if (object.data.length) {
                    const columns = []
                    // eslint-disable-next-line no-restricted-syntax,guard-for-in
                    for (const [key, value] of Object.entries(object.data[0])) {
                        columns.push({
                            label: this.formatTitle(key),
                            field: key,
                            sortable: true,
                            type: this.isNumber(value) ? 'number' : 'string',
                        })
                    }
                    this.columns = columns

                    this.rows = object.data
                }
            } else {
                this.columns = []
                this.rows = []
            }
        },
    },
    created() {
    },
    methods: {
        handleSearch(searching) {
        },
        handleChangePage(page) {
        },
        handlePageChange(active) {
        },
        onSortChange(params) {
        },
        searchTable(row, col, cellValue, searchTerm) {
            return !searchTerm ? true : (cellValue && String(cellValue).toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
        },
        isMobile() {
            return window.innerWidth < 768
        },
        exportCSV() {
            this.exportingCSV = true
            recursivelyExecuteQuery(this.settings.executeQuery, data => {
                this.exportingCSV = false
                if (data && data.length) {
                    const options = {
                        filename: `${this.settings.title} ${moment().format('L')}`,
                        fieldSeparator: ',',
                        quoteStrings: '"',
                        decimalSeparator: '.',
                        showLabels: false,
                        showTitle: false,
                        title: `${this.settings.title} ${moment().format('L')}`,
                        useTextFile: false,
                        useBom: true,
                        useKeysAsHeaders: true,
                        // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
                    }

                    const csvExporter = new ExportToCsv(options)

                    csvExporter.generateCsv(data)
                }
            })
        },
        closeReport() {
            this.$emit('closeReport')
        },
        formatTitle(field) {
            let formattedTitle = this.capitalizeFirstCharacter(field.replace(/([A-Z])/g, ' $1').trim())
            if (this.settings.columns) {
                this.settings.columns.forEach(column => {
                    if (
                        column.field === field
                        && column.label
                    ) {
                        formattedTitle = column.label
                    }
                })
            }

            return formattedTitle
        },
        formatValue(field, value) {
            let formattedValue = value
            if (value && this.settings.columns) {
                this.settings.columns.forEach(column => {
                    if (
                        column.field === field
                        && column.valueFormat
                    ) {
                        // eslint-disable-next-line default-case
                        switch (column.valueFormat) {
                        case 'numberToLocaleString':
                            formattedValue = value.toLocaleString()
                            break
                        case 'datetimeHumanReadable':
                            formattedValue = moment(new Date(value * 1000)).calendar(null, {
                                sameDay: '[Today:] HH:mm',
                                nextDay: '[Tomorrow:] HH:mm',
                                nextWeek: 'dddd HH:mm',
                                lastDay: '[Yesterday:] HH:mm',
                                lastWeek: '[Last:] dddd HH:mm',
                                sameElse: 'L',
                            })
                            break
                        }
                    }
                })
            }

            return formattedValue
        },
        isNumber(n) { return /^-?[\d.]+(?:e-?\d+)?$/.test(n) },
        capitalizeFirstCharacter(str) {
            return str.charAt(0).toUpperCase() + str.slice(1)
        },
    },
}
</script>

<style lang="scss" >
@import '~@core/scss/vue/libs/vue-good-table.scss';
</style>
